import { useState } from 'react';
import * as XLSX from 'xlsx';
import api from '../api/client';

const C = {
  navy: '#1B2A4A', burgundy: '#7A2E3B', gold: '#B8953E', hunter: '#2D5F4A',
  ink: '#1A1A1A', ivory: '#FAF7F2', cream: '#F3EDE4',
};

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`bg-ivory-50 border border-stone-300 rounded-sm ${className}`}>{children}</div>;
}

function SectionTitle({ children, sub }: { children: React.ReactNode; sub?: string }) {
  return (
    <div className="mb-4">
      <h3 className="text-[13px] font-sans font-bold text-stone-500 tracking-widest uppercase border-b-2 border-navy-800 inline-block pb-1">{children}</h3>
      {sub && <p className="text-[13px] font-body text-stone-400 italic mt-1.5">{sub}</p>}
    </div>
  );
}

export default function ReportsPage() {
  const [generating, setGenerating] = useState(false);
  const [pdfOptions, setPdfOptions] = useState({
    includeEstate: true, includeStrategies: true, includeLiquidity: true,
    includeCapitalStructure: true, includeGifts: true,
    brandName: 'Osprey Legacy', preparedFor: '', preparedBy: '',
    projectionHorizon: 6, scenarioType: 'current_law' as string,
    customExemption: '',
  });
  const [exporting, setExporting] = useState('');

  const handleGeneratePDF = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/reports/pdf', pdfOptions, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'osprey-legacy-report.pdf';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to generate PDF. Please try again.');
    }
    setGenerating(false);
  };

  const handleExportExcel = async (dataType: string, filename: string) => {
    setExporting(dataType);
    try {
      const response = await api.get(`/reports/export/${dataType}`);
      const data = response.data.data;
      if (!data || data.length === 0) {
        alert(`No ${dataType} data to export.`);
        setExporting('');
        return;
      }
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, dataType.charAt(0).toUpperCase() + dataType.slice(1));
      XLSX.writeFile(wb, `${filename}.xlsx`);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Please try again.');
    }
    setExporting('');
  };

  const sections = [
    { label: 'Estate Tax Analysis', key: 'includeEstate' },
    { label: 'Strategy Workbench', key: 'includeStrategies' },
    { label: 'Liquidity Runway', key: 'includeLiquidity' },
    { label: 'Capital Structure', key: 'includeCapitalStructure' },
    { label: 'Gift & Exemption', key: 'includeGifts' },
  ];

  const exports = [
    { type: 'assets', label: 'Asset Register', filename: 'osprey-assets', desc: 'All assets with values, types, and entities' },
    { type: 'liabilities', label: 'Debt Schedule', filename: 'osprey-liabilities', desc: 'All obligations with rates and maturities' },
    { type: 'strategies', label: 'Strategy Workbench', filename: 'osprey-strategies', desc: 'Strategies with savings and status' },
    { type: 'gifts', label: 'Gift History', filename: 'osprey-gifts', desc: 'Gift records with exemption usage' },
    { type: 'entities', label: 'Entity Register', filename: 'osprey-entities', desc: 'All entities with types and details' },
  ];

  return (
    <div>
      <div className="grid grid-cols-2 gap-5">
        {/* PDF Report */}
        <Card className="p-6">
          <SectionTitle sub="Generate a branded PDF covering all modules">PDF Report</SectionTitle>

          <div className="space-y-3 mb-5">
            <div>
              <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Firm / Brand Name</label>
              <input type="text" value={pdfOptions.brandName}
                onChange={e => setPdfOptions({ ...pdfOptions, brandName: e.target.value })}
                className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans" />
            </div>
            <div>
              <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Prepared For</label>
              <input type="text" value={pdfOptions.preparedFor} placeholder="e.g., Smith Family Office"
                onChange={e => setPdfOptions({ ...pdfOptions, preparedFor: e.target.value })}
                className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans" />
            </div>
            <div>
              <label className="block text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-1">Prepared By</label>
              <input type="text" value={pdfOptions.preparedBy} placeholder="e.g., Swainson Advisors"
                onChange={e => setPdfOptions({ ...pdfOptions, preparedBy: e.target.value })}
                className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans" />
            </div>
          </div>

          <div className="mb-5">
            <p className="text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-2">Include Sections</p>
            <div className="space-y-2">
              {sections.map(s => (
                <label key={s.key} className="flex items-center gap-2.5 cursor-pointer">
                  <input type="checkbox"
                    checked={(pdfOptions as any)[s.key]}
                    onChange={e => setPdfOptions({ ...pdfOptions, [s.key]: e.target.checked })}
                    className="rounded-sm" />
                  <span className="text-[13px] font-sans text-stone-700">{s.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-5">
            <p className="text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-2">Projection Horizon</p>
            <div className="flex items-center gap-3">
              <input type="range" min={1} max={20} step={1} value={pdfOptions.projectionHorizon}
                onChange={e => setPdfOptions({ ...pdfOptions, projectionHorizon: Number(e.target.value) })}
                className="flex-1" />
              <span className="text-[14px] font-mono font-bold text-stone-700 w-16 text-right">{pdfOptions.projectionHorizon} yrs</span>
            </div>
            <p className="text-[10px] font-sans text-stone-400 mt-1">Controls how far out liquidity projections and timing overlays extend</p>
          </div>

          <div className="mb-5">
            <p className="text-[11px] font-sans font-bold text-stone-400 tracking-wide uppercase mb-2">Legislative Scenario</p>
            <select value={pdfOptions.scenarioType}
              onChange={e => setPdfOptions({ ...pdfOptions, scenarioType: e.target.value })}
              className="w-full px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-sans">
              <option value="current_law">Current Law ($15M exemption — OBBBA)</option>
              <option value="lower_exemption">Lower Exemption Scenario ($7M)</option>
              <option value="custom">Custom Exemption Amount</option>
            </select>
            {pdfOptions.scenarioType === 'custom' && (
              <input type="number" placeholder="Enter exemption amount (e.g., 10000000)"
                value={pdfOptions.customExemption}
                onChange={e => setPdfOptions({ ...pdfOptions, customExemption: e.target.value })}
                className="w-full mt-2 px-3 py-2 bg-ivory-200 border border-stone-300 rounded-sm text-[13px] font-mono" />
            )}
          </div>

          <button onClick={handleGeneratePDF} disabled={generating}
            className="w-full px-5 py-3 rounded-sm text-[13px] font-sans font-bold text-ivory-100 disabled:opacity-50 transition-colors"
            style={{ background: C.navy }}>
            {generating ? 'Generating Report...' : 'Generate PDF Report'}
          </button>
        </Card>

        {/* Data Export */}
        <Card className="p-6">
          <SectionTitle sub="Download any module data as an Excel workbook">Data Export</SectionTitle>
          <div className="space-y-3">
            {exports.map(exp => (
              <div key={exp.type} className="flex items-center justify-between p-3.5 bg-ivory-200 rounded-sm border border-stone-200">
                <div>
                  <p className="text-[13px] font-sans font-bold text-stone-800">{exp.label}</p>
                  <p className="text-[11px] font-sans text-stone-500 mt-0.5">{exp.desc}</p>
                </div>
                <button onClick={() => handleExportExcel(exp.type, exp.filename)}
                  disabled={exporting === exp.type}
                  className="px-4 py-2 rounded-sm text-[11px] font-sans font-bold text-ivory-100 disabled:opacity-50"
                  style={{ background: C.navy }}>
                  {exporting === exp.type ? 'Exporting...' : '.xlsx'}
                </button>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
